import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Bookmark, Check, ZoomIn, Ruler, Package, Palette, Calendar } from "lucide-react";
import { designs, categories } from "../data/designs";
import { useInspiration } from "../hooks/useStore";
import DesignCard from "../components/DesignCard";
import Seo from "../components/Seo";
import { businessLd, breadcrumbLd, designLd } from "../components/StructuredData";

export default function DesignDetailPage() {
  const { slug } = useParams();
  const design = designs.find((p) => p.slug === slug);

  const { list, dispatch } = useInspiration();
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedFinish, setSelectedFinish] = useState(design?.finishes?.[0] || "");
  const [zoomed, setZoomed] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 0, y: 0 });
  const [justSaved, setJustSaved] = useState(false);

  if (!design) {
    return <Navigate to="/portfolio" replace />;
  }

  const isSaved = list.some(
    (i) => i.id === design.id && i.finish === selectedFinish
  );
  const related = designs
    .filter((p) => p.category === design.category && p.id !== design.id)
    .slice(0, 4);

  const images = design.images || [design.image];

  const handleSave = () => {
    dispatch({
      type: "TOGGLE",
      item: {
        id: design.id,
        slug: design.slug,
        name: design.name,
        category: design.category,
        image: design.image,
        finish: selectedFinish,
      },
    });
    if (!isSaved) {
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2200);
    }
  };

  const handleMouseMove = (e) => {
    if (!zoomed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x, y });
  };

  const waMessage = encodeURIComponent(
    `Hello Ananda,\n\nI'd like to discuss the "${design.name}"${
      selectedFinish ? ` (reference finish: ${selectedFinish})` : ""
    } in a free consultation.\n\nCould we schedule a quick call to talk about how it could work in my space?\n\nThank you.`
  );

  const categoryName =
    categories.find((c) => c.slug === design.category)?.name || design.category;

  const jsonLd = [
    businessLd(),
    designLd(design),
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Portfolio", path: "/portfolio" },
      {
        name: categoryName,
        path: `/portfolio?category=${design.category}`,
      },
      { name: design.name, path: `/portfolio/${design.slug}` },
    ]),
  ];

  return (
    <>
      <Seo
        title={`${design.name} — Bespoke ${categoryName} by Ananda Paudel`}
        description={`${design.name} reference design — ${design.description.slice(
          0,
          150
        )}`}
        path={`/portfolio/${design.slug}`}
        image={design.images?.[0] || design.image}
        type="article"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-cream-50 pt-20 sm:pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 border-b border-cream-200">
          <nav className="flex items-center gap-2 font-sans text-xs text-matte-400" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-walnut-500 transition-colors">Home</Link>
            <span>/</span>
            <Link to="/portfolio" className="hover:text-walnut-500 transition-colors">Portfolio</Link>
            <span>/</span>
            <Link
              to={`/portfolio?category=${design.category}`}
              className="hover:text-walnut-500 transition-colors capitalize"
            >
              {design.category.replace(/-/g, " ")}
            </Link>
            <span>/</span>
            <span className="text-matte-700">{design.name}</span>
          </nav>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div className="flex gap-4">
              <div className="flex flex-col gap-3 w-16 sm:w-20 flex-shrink-0">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`aspect-square overflow-hidden rounded-sm border-2 transition-all ${
                      i === selectedImage
                        ? "border-walnut-400"
                        : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                    aria-label={`Show image ${i + 1}`}
                  >
                    <img
                      src={img}
                      alt={`${design.name} thumbnail ${i + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      width="80"
                      height="80"
                    />
                  </button>
                ))}
              </div>

              <div className="flex-1 relative">
                <div
                  className={`relative overflow-hidden rounded-sm aspect-[4/5] cursor-zoom-in ${
                    zoomed ? "cursor-zoom-out" : ""
                  }`}
                  onClick={() => setZoomed(!zoomed)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={() => setZoomed(false)}
                >
                  <img
                    src={images[selectedImage]}
                    alt={`${design.name} — bespoke ${categoryName.toLowerCase()} by Ananda Paudel, Nepal`}
                    className="w-full h-full object-cover transition-transform duration-300"
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                    width="900"
                    height="1125"
                    style={
                      zoomed
                        ? {
                            transform: "scale(2)",
                            transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`,
                          }
                        : {}
                    }
                  />
                  <div className="absolute top-3 right-3 bg-white/90 rounded-full w-8 h-8 flex items-center justify-center text-matte-500 pointer-events-none">
                    <ZoomIn size={14} />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className="font-sans text-xs tracking-widest text-walnut-400 uppercase mb-3">
                Reference Design · {categoryName}
              </p>
              <h1 className="font-serif text-3xl sm:text-4xl text-matte-900 font-light mb-4">
                {design.name}
              </h1>

              <div className="bg-walnut-50 border border-walnut-200 rounded-sm px-4 py-3 mb-6">
                <p className="font-sans text-sm text-walnut-700">
                  <span className="font-medium">This is a reference design.</span>{" "}
                  We tailor dimensions, materials, and finishes to your space.
                  Pricing is shared transparently in your free consultation.
                </p>
              </div>

              <p className="font-sans text-sm sm:text-base text-matte-500 leading-relaxed mb-8">
                {design.description}
              </p>

              {design.finishes?.length > 0 && (
                <div className="mb-7">
                  <p className="font-sans text-xs tracking-widest text-matte-400 uppercase mb-3">
                    Reference finish:{" "}
                    <span className="text-matte-700 normal-case tracking-normal">
                      {selectedFinish}
                    </span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {design.finishes.map((finish) => (
                      <button
                        key={finish}
                        onClick={() => setSelectedFinish(finish)}
                        className={`font-sans text-sm px-4 py-2 border rounded-sm transition-all ${
                          selectedFinish === finish
                            ? "border-matte-900 bg-matte-900 text-cream-100"
                            : "border-cream-300 text-matte-600 hover:border-walnut-400"
                        }`}
                      >
                        {finish}
                      </button>
                    ))}
                  </div>
                  <p className="font-sans text-xs text-matte-400 mt-2">
                    Custom finishes welcome — chosen during the design phase.
                  </p>
                </div>
              )}

              <div className="flex gap-3 mb-4">
                <Link
                  to="/book"
                  className="flex-1 flex items-center justify-center gap-2 py-4 font-sans text-sm tracking-wider transition-all duration-300 bg-matte-900 text-cream-100 hover:bg-walnut-700"
                >
                  <Calendar size={16} />
                  Book a Consultation About This
                </Link>
                <button
                  onClick={handleSave}
                  className={`w-14 h-14 flex items-center justify-center border rounded-sm transition-all ${
                    isSaved
                      ? "bg-walnut-500 border-walnut-500 text-white"
                      : "border-cream-300 text-matte-500 hover:border-walnut-400 hover:text-walnut-500"
                  }`}
                  aria-label={isSaved ? "Remove from inspiration list" : "Save to inspiration list"}
                  title={isSaved ? "Saved to your inspiration list" : "Save to your inspiration list"}
                >
                  {justSaved ? (
                    <Check size={18} />
                  ) : (
                    <Bookmark size={18} fill={isSaved ? "currentColor" : "none"} />
                  )}
                </button>
              </div>

              <a
                href={`https://wa.me/9779841488271?text=${waMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 border border-[#25D366] text-[#25D366] font-sans text-sm tracking-wide py-3.5 hover:bg-[#25D366] hover:text-white transition-colors rounded-sm mb-8"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Or WhatsApp Ananda about this design
              </a>

              <div className="border-t border-cream-200 pt-6 space-y-4">
                {design.material && (
                  <div className="flex items-start gap-3">
                    <Package size={16} className="text-walnut-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-sans text-xs text-matte-400 uppercase tracking-wider mb-0.5">
                        Reference materials
                      </p>
                      <p className="font-sans text-sm text-matte-700">{design.material}</p>
                    </div>
                  </div>
                )}
                {design.dimensions && (
                  <div className="flex items-start gap-3">
                    <Ruler size={16} className="text-walnut-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-sans text-xs text-matte-400 uppercase tracking-wider mb-0.5">
                        Reference dimensions
                      </p>
                      <p className="font-sans text-sm text-matte-700">{design.dimensions}</p>
                      <p className="font-sans text-xs text-matte-400 mt-1">
                        Final dimensions are sized to your space during design.
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <Palette size={16} className="text-walnut-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-sans text-xs text-matte-400 uppercase tracking-wider mb-0.5">
                      Customisation
                    </p>
                    <p className="font-sans text-sm text-matte-700">
                      Dimensions, finish, material, and hardware are all
                      decided in the design phase. Ananda works directly with
                      craftsmen to build exactly what you need.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-5 py-5 border-t border-cream-200">
                {[
                  "Bespoke build",
                  "Free delivery — Kathmandu",
                  "Factory-direct pricing",
                  "40+ years expertise",
                ].map((b) => (
                  <div key={b} className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-walnut-100 flex items-center justify-center">
                      <Check size={9} className="text-walnut-500" />
                    </div>
                    <span className="font-sans text-xs text-matte-500">{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-20 sm:mt-28">
              <h2 className="font-serif text-3xl sm:text-4xl text-matte-900 font-light mb-2">
                Related designs
              </h2>
              <p className="font-sans text-sm text-matte-400 mb-10">
                More reference work in {categoryName.toLowerCase()}
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                {related.map((p) => (
                  <DesignCard key={p.id} design={p} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
